import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { DraggableList } from "src/components";
import { MetricDto, useGetMetricCollection, useReorderMetricCollection } from "src/http";
import { lexorank } from "src/utilities";

interface Props {
  visible: boolean;
  metricCollectionGuid: string;
  onSave: () => void;
  onCancel: () => void;
}

const ReorderMetricCollectionModal = (props: Props) => {
  const [localState, setLocalState] = useState<MetricDto[]>([]);
  const { data, isLoading } = useGetMetricCollection(props.metricCollectionGuid);
  const { mutate: reorderMetrics, isLoading: isReordering } = useReorderMetricCollection();

  useEffect(() => {
    setLocalState(data?.sort((a, b) => a.sortOrder.localeCompare(b.sortOrder)) ?? []);
  }, [props.visible, data]);

  function handleReorder(sourceIndex: number, destinationIndex: number) {
    setLocalState((initialState) => {
      const result = produce(initialState, (draft) => {
        const newSortOrder = lexorank.getRank(draft, sourceIndex, destinationIndex);
        const [moved] = draft.splice(sourceIndex, 1);
        moved.sortOrder = newSortOrder;
        draft.splice(destinationIndex, 0, moved);

        return draft;
      });

      return result;
    });
  }

  function handleSave() {
    const metricSortOrders = localState.map((metric) => ({
      metricGuid: metric.guid,
      sortOrder: metric.sortOrder,
    }));

    reorderMetrics(
      {
        metricCollectionGuid: props.metricCollectionGuid,
        metricSortOrders,
      },
      {
        onSuccess: () => {
          props.onSave();
        },
      }
    );
  }

  function handleClose(_event: object, reason?: "backdropClick" | "escapeKeyDown") {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    props.onCancel();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Reorder Metrics</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ px: 1 }}>Change the order of metrics on this page.</Typography>
          <Box>
            <Divider variant="dashed" sx={{ mx: 1 }} />
            <DraggableList
              id="reorderMetrics"
              isLoading={isLoading}
              dataSource={localState}
              renderItem={(item) => (
                <Stack>
                  <Typography sx={{ py: 0.5 }}>{item.title}</Typography>
                  <Divider variant="dashed" sx={{ mr: 1 }} />
                </Stack>
              )}
              onReorder={handleReorder}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isLoading || isReordering}
          endIcon={isReordering ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { ReorderMetricCollectionModal };
